/**
 * Admin Ministries Page: list of ministries.
 * Native mobile app style. Load once, filter client-side to avoid re-renders on filter switch.
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconPlus,
  IconAlertTriangle,
  IconUsers,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic } from "../../../lib/telegram.ts";
import { getErrorMessage } from "../../../lib/graphql.ts";
import { useAdminMinistries } from "./hooks";

type FilterMode = "all" | "active" | "inactive";

export function AdminMinistriesPage() {
  const navigate = useNavigate();
  const tg = getTelegram();

  const { ministries: list, isLoading, error, refetch } = useAdminMinistries();
  const [filterMode, setFilterMode] = useState<FilterMode>("all");

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  const filteredList = useMemo(() => {
    if (filterMode === "all") return list;
    if (filterMode === "active") return list.filter((m) => m.isActive);
    return list.filter((m) => !m.isActive);
  }, [list, filterMode]);

  const activeCount = useMemo(
    () => list.filter((m) => m.isActive).length,
    [list],
  );

  const showFullLoader = isLoading && list.length === 0;
  if (showFullLoader) return <PageLoader message="Загрузка служений..." />;

  if (error) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">
            Ошибка загрузки
          </h2>
          <p className="text-sm text-slate-500 mb-4">{getErrorMessage(error, "Не удалось загрузить служения")}</p>
          <button onClick={refetch} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600">
            <IconUsers className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              Служения
            </h1>
            <p className="text-[13px] text-slate-500">
              {activeCount} активных из {list.length}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              haptic("light");
              navigate("/admin/ministries/new");
            }}
            className="w-10 h-10 rounded-xl bg-[var(--color-accent)] text-white flex items-center justify-center active:scale-95 transition-transform flex-shrink-0"
            aria-label="Добавить служение"
          >
            <IconPlus className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <button
            type="button"
            onClick={() => {
              haptic("light");
              setFilterMode("all");
            }}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors active:scale-[0.98] min-h-[32px] ${
              filterMode === "all"
                ? "bg-slate-700 text-white"
                : "bg-slate-100 text-slate-600 active:bg-slate-200"
            }`}
          >
            Все
          </button>
          <button
            type="button"
            onClick={() => {
              haptic("light");
              setFilterMode("active");
            }}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors active:scale-[0.98] min-h-[32px] ${
              filterMode === "active"
                ? "bg-emerald-600 text-white"
                : "bg-slate-100 text-slate-600 active:bg-slate-200"
            }`}
          >
            Активные
          </button>
          <button
            type="button"
            onClick={() => {
              haptic("light");
              setFilterMode("inactive");
            }}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors active:scale-[0.98] min-h-[32px] ${
              filterMode === "inactive"
                ? "bg-amber-600 text-white"
                : "bg-slate-100 text-slate-600 active:bg-slate-200"
            }`}
          >
            Неактивные
          </button>
          {isLoading && list.length > 0 && (
            <span className="text-[12px] text-slate-400 self-center">
              Обновление...
            </span>
          )}
        </div>

        {filteredList.length === 0 && !isLoading && (
          <div className="card p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-slate-100 flex items-center justify-center">
              <IconUsers className="w-6 h-6 text-slate-400" />
            </div>
            <h3 className="text-[15px] font-semibold text-slate-900 mb-2">
              Нет служений
            </h3>
            <p className="text-[13px] text-slate-500 mb-4">
              {filterMode !== "all"
                ? "Попробуйте изменить фильтр"
                : "Создайте первое служение"}
            </p>
            {filterMode !== "all" ? (
              <button
                type="button"
                onClick={() => setFilterMode("all")}
                className="text-[14px] text-[var(--color-accent)] font-medium"
              >
                Сбросить фильтр
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  haptic("light");
                  navigate("/admin/ministries/new");
                }}
                className="btn-primary"
              >
                Добавить служение
              </button>
            )}
          </div>
        )}

        {filteredList.length > 0 && (
          <div className="card overflow-hidden">
            {filteredList.map((item, idx) => {
              const descSnippet = item.description
                ? item.description.length > 60
                  ? item.description.slice(0, 60) + "..."
                  : item.description
                : null;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    haptic("light");
                    navigate(`/admin/ministries/${item.id}`);
                  }}
                  className={`w-full flex items-center gap-3 min-h-[72px] px-4 py-3 active:bg-slate-50 transition-colors text-left ${
                    idx > 0 ? "border-t border-slate-100" : ""
                  } ${!item.isActive ? "opacity-70" : ""}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <IconUsers className="w-5 h-5 text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-semibold text-slate-900 text-[15px] truncate">
                        {item.name}
                      </span>
                      {!item.isActive && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold text-slate-400 bg-slate-100 rounded shrink-0">
                          СКРЫТО
                        </span>
                      )}
                    </div>
                    <p className="text-[12px] text-slate-500 truncate">
                      {item.code}
                      {descSnippet ? ` · ${descSnippet}` : ""}
                    </p>
                    <span className="text-[11px] text-slate-400">
                      {item.memberCount} участников
                    </span>
                  </div>
                  <svg
                    className="w-5 h-5 text-slate-300 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              );
            })}
          </div>
        )}

        <div className="h-6" />
      </div>
    </Layout>
  );
}
